import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import RichEditor from "@/components/RichEditor";
import StoriesTravelMap from "@/components/StoriesTravelMap";
import { NATUREPULSE_URL } from "@/lib/site";
import {
  Newspaper, Books, Article, Info, Scales, PencilSimple, Plus, Trash,
  EyeSlash, Eye, Users, PaperPlaneTilt, X, ArrowLeft, Broadcast,
  Images, UploadSimple, Camera, DownloadSimple, ShareNetwork,
} from "@phosphor-icons/react";

// -------------------------------------------------------------------------
// Config
// -------------------------------------------------------------------------
const SURFACES = [
  { key: "news",         label: "News",         icon: Newspaper, page_type: "news" },
  { key: "articles",     label: "Articles",     icon: Article,   page_type: "articles" },
  { key: "observations", label: "Observations", icon: Books,     page_type: null }, // moderation-only
  { key: "photos",       label: "Photos",       icon: Images,    page_type: null }, // library
  { key: "legal",        label: "Legal",        icon: Scales,    page_type: "legal" },
  { key: "about",        label: "About",        icon: Info,      page_type: "about" },
];

const CATEGORY_HINTS = {
  news:     ["climate", "wildlife", "ocean", "policy", "science", "food", "agriculture"],
  articles: ["editorial", "field-notes", "explainer", "opinion", "food", "crops"],
  legal:    ["terms", "privacy", "cookies", "dpdp", "coppa", "ferpa", "gdpr"],
  about:    ["about", "team", "mission", "contact", "press"],
};

const SYND_TARGET_HINTS = ["foodpulse", "farmerspulse", "alumnipulse", "indiaalumni", "socialpulse"];

// -------------------------------------------------------------------------
// Dashboard shell — /admin/cms
// -------------------------------------------------------------------------
export default function AdminCms() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("news");

  useEffect(() => {
    http.get("/cms/pages/stats/overview").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (loading) return <div className="min-h-screen pt-32 flex items-center justify-center font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">Loading…</div>;
  const isEditor = user && (user.is_admin || user.role === "editor");
  if (!isEditor) return <Navigate to="/" replace />;

  const allowed = user.is_admin
    ? SURFACES
    : SURFACES.filter((s) => {
        const surfs = user.editor_surfaces || [];
        return surfs.includes("all") || surfs.includes(s.key);
      });

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-10 max-w-[1400px] mx-auto" data-testid="admin-cms-page">
      <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        NaturePulse · Unified CMS
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-[0.95] tracking-tighter">Content Studio.</h1>
          <p className="mt-3 text-[#3A3830] max-w-2xl">
            Manage News, Articles, Observations, Legal and About pages from one desk.
            Powered by the Tiptap rich editor. Every publish is instant.
          </p>
        </div>
        {user.is_admin && (
          <button
            data-testid="cms-editors-tab"
            onClick={() => setTab("editors")}
            className={
              "inline-flex items-center gap-2 rounded-full border border-black/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase " +
              (tab === "editors"
                ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent"
                : "bg-white hover:bg-black/5")
            }
          >
            <Users size={14} weight="fill" /> Editors
          </button>
        )}
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cms-stats">
          {SURFACES.filter((s) => s.page_type).map((s) => {
            const st = stats.pages?.[s.page_type] || { draft: 0, published: 0 };
            return (
              <StatCard key={s.key} icon={s.icon} label={s.label}
                        published={st.published || 0} drafts={st.draft || 0}
                        testid={`cms-stat-${s.key}`} />
            );
          })}
        </div>
      )}

      {/* Where our stories travel — live SpLink scan map */}
      <div className="mt-8" data-testid="cms-travel-map-widget">
        <div className="flex items-baseline justify-between mb-3">
          <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Where our stories travel
          </div>
          <a
            href="/where-our-stories-travel"
            className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[#3A3830] hover:text-[color:var(--np-brand,#B85F2A)]"
            data-testid="cms-travel-map-fullview"
          >
            Full view →
          </a>
        </div>
        <StoriesTravelMap variant="widget" days={30} />
      </div>

      {/* Tabs */}
      <div className="mt-8 flex flex-wrap gap-2 border-b border-black/10 pb-3">
        {allowed.map((s) => (
          <button
            key={s.key}
            onClick={() => setTab(s.key)}
            data-testid={`cms-tab-${s.key}`}
            className={
              "inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase " +
              (tab === s.key
                ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
                : "bg-white border border-black/15 hover:bg-black/5")
            }
          >
            <s.icon size={13} weight="fill" /> {s.label}
          </button>
        ))}
      </div>

      {/* Panels */}
      <div className="mt-6">
        {tab === "editors" && user.is_admin && <EditorsPanel />}
        {tab === "observations" && <ObservationsPanel />}
        {tab === "photos" && <PhotosPanel />}
        {["news", "articles", "legal", "about"].includes(tab) && (
          <PagesPanel key={tab} pageType={tab} />
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, published, drafts, testid }) => (
  <div className="rounded-2xl border border-black/10 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
      <Icon size={13} weight="fill" /> {label}
    </div>
    <div className="mt-1 flex items-baseline gap-2">
      <div className="font-serif-display text-3xl leading-none">{published}</div>
      <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">published</div>
    </div>
    <div className="mt-1 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">{drafts} draft{drafts === 1 ? "" : "s"}</div>
  </div>
);

// -------------------------------------------------------------------------
// Pages panel (News / Articles / Legal / About)
// -------------------------------------------------------------------------
function PagesPanel({ pageType }) {
  const [pages, setPages] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [shares, setShares] = useState({}); // { [absolute_url]: {clicks, last_click_at, short_url} }

  const load = async () => {
    const params = new URLSearchParams();
    params.set("page_type", pageType);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const { data } = await http.get(`/cms/pages/admin?${params}`);
    setPages(data.pages || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pageType, status, q]);

  // Fetch SpLink share counts for every visible page (batched, 60s server-side cache).
  useEffect(() => {
    if (!pages.length) { setShares({}); return; }
    const urls = pages.map((p) => `${NATUREPULSE_URL}/${pageType}/${p.slug}`);
    let cancelled = false;
    http.post("/short/stats/bulk", { urls })
      .then(({ data }) => { if (!cancelled) setShares(data.results || {}); })
      .catch(() => {});
    const t = setInterval(() => {
      http.post("/short/stats/bulk", { urls })
        .then(({ data }) => { if (!cancelled) setShares(data.results || {}); })
        .catch(() => {});
    }, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [pages, pageType]);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await http.delete(`/cms/pages/${pageType}/${p.slug}`);
      toast.success("Page deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onTogglePublish = async (p) => {
    try {
      if (p.status === "published") {
        await http.post(`/cms/pages/${pageType}/${p.slug}/unpublish`);
        toast.success("Unpublished");
      } else {
        await http.post(`/cms/pages/${pageType}/${p.slug}/publish`);
        toast.success("Published");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  if (creating || editing) {
    return (
      <PageEditor
        pageType={pageType}
        page={editing}
        onClose={() => { setCreating(false); setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid={`cms-panel-${pageType}`}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          data-testid="cms-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search title or slug…"
          className="flex-1 min-w-[240px] rounded-full border border-black/15 bg-white px-4 py-2 text-sm"
        />
        <select
          data-testid="cms-status-filter"
          value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-full border border-black/15 bg-white px-3 py-2 text-sm"
        >
          <option value="">All</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <button
          data-testid="cms-new-page"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
        >
          <Plus size={13} weight="bold" /> New
        </button>
      </div>

      {pages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-black/15 bg-white/60 p-12 text-center">
          <p className="font-serif-display text-xl">No {pageType} pages yet.</p>
          <p className="mt-1 text-sm text-[#3A3830]">Hit "New" to create the first one.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {pages.map((p) => (
            <li
              key={p.id}
              data-testid={`cms-row-${p.slug}`}
              className="rounded-2xl border border-black/10 bg-white p-4 flex flex-wrap items-center gap-3"
            >
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={
                      "rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase " +
                      (p.status === "published"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200")
                    }
                  >{p.status}</span>
                  {(p.syndication_targets || []).length > 0 && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase bg-[#FBF2E1] text-[color:var(--np-brand,#B85F2A)] border border-black/10"
                      title={`Syndicated to: ${(p.syndication_targets || []).join(", ")}`}
                    >
                      <Broadcast size={11} weight="fill" /> {(p.syndication_targets || []).length}
                    </span>
                  )}
                  <SharesBadge stats={shares[`${NATUREPULSE_URL}/${pageType}/${p.slug}`]} />
                </div>
                <div className="mt-1 font-serif-display text-lg leading-tight">{p.title}</div>
                <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">
                  /{pageType}/{p.slug} · {p.word_count || 0} words · updated {p.updated_at?.slice(0, 10)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`cms-toggle-publish-${p.slug}`}
                  onClick={() => onTogglePublish(p)}
                  className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase hover:bg-black/5"
                >
                  {p.status === "published" ? <><EyeSlash size={12} weight="bold" /> Unpublish</> : <><Eye size={12} weight="bold" /> Publish</>}
                </button>
                <button
                  data-testid={`cms-edit-${p.slug}`}
                  onClick={() => setEditing(p)}
                  className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase hover:bg-black/5"
                >
                  <PencilSimple size={12} weight="bold" /> Edit
                </button>
                <button
                  data-testid={`cms-delete-${p.slug}`}
                  onClick={() => onDelete(p)}
                  className="rounded-full border border-red-200 bg-red-50 text-red-700 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase hover:bg-red-100"
                >
                  <Trash size={12} weight="bold" /> Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// SharesBadge — shows SpLink scan count + relative "last scanned" time
// -------------------------------------------------------------------------
function SharesBadge({ stats }) {
  if (!stats) return null;
  const clicks = stats.clicks || 0;
  const last = stats.last_click_at || stats.last_scanned_at;
  const title = last ? `Last scanned ${relativeTime(last)}` : "No scans yet";
  const hasScans = clicks > 0;
  return (
    <span
      data-testid="cms-shares-badge"
      title={title}
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase border " +
        (hasScans
          ? "bg-[#E8F5EE] text-emerald-800 border-emerald-200"
          : "bg-white text-[#8E8B7F] border-black/10")
      }
    >
      <ShareNetwork size={11} weight="fill" /> {clicks}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return "never";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "unknown";
  }
}

// -------------------------------------------------------------------------
// Page editor (create + edit)
// -------------------------------------------------------------------------
function PageEditor({ pageType, page, onClose }) {
  const isNew = !page;
  const [title, setTitle] = useState(page?.title || "");
  const [subtitle, setSubtitle] = useState(page?.subtitle || "");
  const [slug, setSlug] = useState(page?.slug || "");
  const [category, setCategory] = useState(page?.category || "");
  const [tags, setTags] = useState((page?.tags || []).join(", "));
  const [coverImage, setCoverImage] = useState(page?.cover_image || "");
  const [excerpt, setExcerpt] = useState(page?.excerpt || "");
  const [body, setBody] = useState(page?.body_html || "");
  const [status, setStatus] = useState(page?.status || "draft");
  const [syndTargets, setSyndTargets] = useState((page?.syndication_targets || []).join(", "));
  const [syndDisabled, setSyndDisabled] = useState(!!page?.syndication_disabled);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (title.trim().length < 3) { toast.error("Title too short"); return; }
    if ((body || "").replace(/<[^>]+>/g, "").trim().length < 20) { toast.error("Body too short (min 20 chars of visible text)"); return; }
    setSaving(true);
    const tagArr = tags.split(",").map((t) => t.trim()).filter(Boolean);
    const syndArr = syndTargets.split(",").map((t) => t.trim()).filter(Boolean);
    const payload = {
      title,
      subtitle: subtitle || null,
      body_html: body,
      excerpt: excerpt || null,
      cover_image: coverImage || null,
      category: category || null,
      tags: tagArr,
      status,
      syndication_targets: syndArr.length ? syndArr : null,
      syndication_disabled: syndDisabled,
    };
    try {
      if (isNew) {
        await http.post(`/cms/pages`, { page_type: pageType, slug: slug || undefined, ...payload });
        toast.success("Page created");
      } else {
        await http.patch(`/cms/pages/${pageType}/${page.slug}`, payload);
        toast.success("Page updated");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cms-page-editor">
      <button
        onClick={onClose}
        data-testid="cms-editor-back"
        className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830] hover:text-[#151312]"
      >
        <ArrowLeft size={12} weight="bold" /> Back to {pageType}
      </button>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="min-w-0 space-y-4">
          <input
            data-testid="cms-editor-title"
            value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 font-serif-display text-2xl"
          />
          <input
            data-testid="cms-editor-subtitle"
            value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Subtitle (optional)"
            className="w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-base italic"
          />
          <RichEditor value={body} onChange={setBody} testid="cms-editor-body" placeholder="Write your article. Use the toolbar to add images, YouTube, podcasts…" />
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-black/10 bg-white p-4">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">Publish</div>
            <label className="block text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-1">Status</label>
            <select
              data-testid="cms-editor-status"
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <button
              onClick={save}
              disabled={saving}
              data-testid="cms-editor-save"
              className="mt-4 w-full rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60"
            >
              {saving ? "Saving…" : (isNew ? "Create" : "Save changes")}
            </button>
          </div>

          <div className="rounded-2xl border border-black/10 bg-white p-4">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">Metadata</div>
            {isNew && (
              <>
                <label className="block text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-1">Slug</label>
                <input
                  data-testid="cms-editor-slug"
                  value={slug} onChange={(e) => setSlug(e.target.value)}
                  placeholder="auto from title"
                  className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-3"
                />
              </>
            )}
            <label className="block text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-1">Category</label>
            <input
              data-testid="cms-editor-category"
              value={category} onChange={(e) => setCategory(e.target.value)}
              list={`cat-hints-${pageType}`}
              placeholder="e.g. food, crops, climate…"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-3"
            />
            <datalist id={`cat-hints-${pageType}`}>
              {(CATEGORY_HINTS[pageType] || []).map((c) => <option key={c} value={c} />)}
            </datalist>
            <label className="block text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-1">Tags (comma-separated)</label>
            <input
              data-testid="cms-editor-tags"
              value={tags} onChange={(e) => setTags(e.target.value)}
              placeholder="food, harvest, farmer"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-3"
            />
            <label className="block text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-1">Cover image URL</label>
            <input
              data-testid="cms-editor-cover"
              value={coverImage} onChange={(e) => setCoverImage(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-3"
            />
            <label className="block text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-1">Excerpt</label>
            <textarea
              data-testid="cms-editor-excerpt"
              value={excerpt} onChange={(e) => setExcerpt(e.target.value)}
              rows={2}
              placeholder="One-line pull-quote for the list card"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
            />
          </div>

          <div className="rounded-2xl border border-black/10 bg-white p-4">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
              <Broadcast size={12} weight="fill" className="inline mr-1" /> Cross-site syndication
            </div>
            <p className="text-xs text-[#3A3830] mb-3">
              Content tagged with food / crops / farming categories is auto-syndicated to <b>foodpulse.net</b>.
              Override or disable below.
            </p>
            <label className="block text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-1">Manual targets (comma-separated)</label>
            <input
              data-testid="cms-editor-syndication"
              value={syndTargets} onChange={(e) => setSyndTargets(e.target.value)}
              list="synd-target-hints"
              placeholder="foodpulse, farmerspulse (blank = auto)"
              className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-2"
            />
            <datalist id="synd-target-hints">
              {SYND_TARGET_HINTS.map((c) => <option key={c} value={c} />)}
            </datalist>
            <label className="inline-flex items-center gap-2 text-sm mt-2">
              <input
                type="checkbox"
                data-testid="cms-editor-syndication-disabled"
                checked={syndDisabled}
                onChange={(e) => setSyndDisabled(e.target.checked)}
              />
              Disable syndication for this page
            </label>
          </div>
        </aside>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Editors panel (admin-only)
// -------------------------------------------------------------------------
function EditorsPanel() {
  const [invites, setInvites] = useState([]);
  const [editors, setEditors] = useState([]);
  const [email, setEmail] = useState("");
  const [surfaces, setSurfaces] = useState(["all"]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [i, e] = await Promise.all([
      http.get(`/editors/invitations`),
      http.get(`/editors`),
    ]);
    setInvites(i.data.invitations || []);
    setEditors(e.data.editors || []);
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.includes("@")) { toast.error("Valid email required"); return; }
    setBusy(true);
    try {
      const { data } = await http.post(`/editors/invitations`, {
        email,
        surfaces: surfaces.length ? surfaces : ["all"],
        note: note || null,
      });
      toast.success(data.granted_immediately ? "Editor role granted immediately" : "Invitation sent — role granted on next sign-in");
      setEmail(""); setNote(""); setSurfaces(["all"]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (uid) => {
    if (!window.confirm("Revoke editor role for this user?")) return;
    try {
      await http.delete(`/editors/${uid}`);
      toast.success("Editor revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const deleteInvite = async (id) => {
    try {
      await http.delete(`/editors/invitations/${id}`);
      toast.success("Invitation removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleSurface = (key) => {
    setSurfaces((s) => {
      if (s.includes("all")) return [key];
      const next = s.includes(key) ? s.filter((x) => x !== key) : [...s, key];
      return next.length ? next : ["all"];
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="cms-editors-panel">
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          <PaperPlaneTilt size={13} weight="fill" className="inline mr-1" /> Invite an editor
        </div>
        <p className="text-xs text-[#3A3830] mb-3">
          Enter the email of someone with a registered NaturePulse / SocialPulse account.
          They get editor permissions immediately. Unregistered emails get the role on first sign-in.
        </p>
        <input
          data-testid="cms-invite-email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="editor@example.com" type="email"
          className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-3"
        />
        <div className="mb-3">
          <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] mb-2">Surfaces</div>
          <div className="flex flex-wrap gap-1.5">
            <button
              data-testid="cms-invite-surface-all"
              onClick={() => setSurfaces(["all"])}
              className={
                "rounded-full px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase border " +
                (surfaces.includes("all")
                  ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent"
                  : "bg-white text-[#151312] border-black/15")
              }
            >All CMS</button>
            {["news", "articles", "observations", "legal", "about", "crops"].map((s) => (
              <button
                key={s}
                data-testid={`cms-invite-surface-${s}`}
                onClick={() => toggleSurface(s)}
                className={
                  "rounded-full px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase border " +
                  (surfaces.includes(s) && !surfaces.includes("all")
                    ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent"
                    : "bg-white text-[#151312] border-black/15")
                }
              >{s}</button>
            ))}
          </div>
        </div>
        <textarea
          data-testid="cms-invite-note"
          value={note} onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Optional note (visible only to admins)"
          className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-3"
        />
        <button
          data-testid="cms-invite-submit"
          disabled={busy}
          onClick={invite}
          className="w-full rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60"
        >
          {busy ? "Sending…" : "Send invitation"}
        </button>
      </div>

      <div className="space-y-6">
        <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="cms-active-editors">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
            Active editors ({editors.length})
          </div>
          {editors.length === 0 ? (
            <div className="text-xs text-[#8E8B7F] italic">No editors yet.</div>
          ) : (
            <ul className="space-y-2">
              {editors.map((e) => (
                <li key={e.firebase_uid} data-testid={`cms-editor-row-${e.firebase_uid}`}
                    className="flex items-center gap-2 text-sm border-b border-black/5 pb-2 last:border-none">
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{e.name || e.email}</div>
                    <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F] truncate">
                      {e.email} · {(e.editor_surfaces || []).join(", ") || "—"}
                    </div>
                  </div>
                  <button
                    data-testid={`cms-editor-revoke-${e.firebase_uid}`}
                    onClick={() => revoke(e.firebase_uid)}
                    className="rounded-full border border-red-200 bg-red-50 text-red-700 px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-red-100"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="cms-invitations">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
            Invitations ({invites.length})
          </div>
          {invites.length === 0 ? (
            <div className="text-xs text-[#8E8B7F] italic">No invitations sent yet.</div>
          ) : (
            <ul className="space-y-2">
              {invites.map((i) => (
                <li key={i.id} data-testid={`cms-invite-row-${i.id}`}
                    className="flex items-center gap-2 text-sm border-b border-black/5 pb-2 last:border-none">
                  <span
                    className={
                      "rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase " +
                      (i.status === "accepted"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200")
                    }
                  >{i.status}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{i.email}</div>
                    <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F] truncate">
                      {(i.surfaces || []).join(", ")} · {i.created_at?.slice(0, 10)}
                    </div>
                  </div>
                  <button
                    data-testid={`cms-invite-delete-${i.id}`}
                    onClick={() => deleteInvite(i.id)}
                    className="text-[#8E8B7F] hover:text-red-700"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Photos panel — local library + photopulse.pro browse + upload
// -------------------------------------------------------------------------
const CATEGORIES = [
  "sunsets", "plants", "flowers", "animals", "birds", "insects",
  "trees", "landscape", "rocks", "hills", "mountains",
  "rain", "snow", "hail", "clouds",
  "cityscape", "streets", "roads",
  "beach", "lake", "rivers", "wetlands",
  "agriculture", "wildlife", "forests", "other",
];

function PhotosPanel() {
  const [tab, setTab] = useState("local");
  const [local, setLocal] = useState([]);
  const [remote, setRemote] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const loadLocal = async () => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    params.set("limit", "100");
    const { data } = await http.get(`/cms/photos?${params}`);
    setLocal(data.photos || []);
  };
  const loadRemote = async () => {
    try {
      const { data } = await http.get(`/photopulse/feed?limit=40`);
      setRemote(data.photos || []);
    } catch (e) {
      toast.error("PhotoPulse feed unavailable");
      setRemote([]);
    }
  };
  useEffect(() => {
    if (tab === "local") loadLocal();
    else loadRemote();
    // eslint-disable-next-line
  }, [tab, category]);

  const upload = async (file) => {
    if (!file) return;
    if (!category) { toast.error("Pick a category before uploading"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image must be under 20 MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await http.post(`/cms/photos`, {
          image_base64: reader.result,
          content_type: file.type,
          category,
          tags: [],
        });
        toast.success("Uploaded");
        setLocal((l) => [data, ...l]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const importFromPP = async (photo) => {
    if (!category) { toast.error("Pick a category first"); return; }
    setImportingId(photo.id || photo.image_url);
    try {
      const { data } = await http.post(`/cms/photos/import-from-photopulse`, {
        image_url: photo.image_url,
        category,
        tags: photo.tags || [],
        caption: photo.title || photo.caption || null,
        credit: photo.photographer || "PhotoPulse.pro",
        location: photo.location || null,
        photopulse_id: photo.id || null,
      });
      toast.success("Imported to local library");
      setLocal((l) => [data, ...l]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImportingId(null);
    }
  };

  const removeLocal = async (id) => {
    if (!window.confirm("Remove this photo from the library?")) return;
    try {
      await http.delete(`/cms/photos/${id}`);
      toast.success("Removed");
      setLocal((l) => l.filter((p) => p.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filteredLocal = q
    ? local.filter((p) => (p.caption || "").toLowerCase().includes(q.toLowerCase()) || (p.tags || []).some((t) => t.includes(q.toLowerCase())))
    : local;
  const filteredRemote = q
    ? remote.filter((p) => (p.title || p.caption || "").toLowerCase().includes(q.toLowerCase()))
    : remote;

  return (
    <div data-testid="cms-panel-photos">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          data-testid="cms-photos-tab-local"
          onClick={() => setTab("local")}
          className={
            "rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase " +
            (tab === "local"
              ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
              : "bg-white border border-black/15 hover:bg-black/5")
          }
        >Local library</button>
        <button
          data-testid="cms-photos-tab-photopulse"
          onClick={() => setTab("photopulse")}
          className={
            "rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase " +
            (tab === "photopulse"
              ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
              : "bg-white border border-black/15 hover:bg-black/5")
          }
        >PhotoPulse.pro ↗</button>
        <input
          data-testid="cms-photos-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search caption / tag…"
          className="flex-1 min-w-[220px] rounded-full border border-black/15 bg-white px-4 py-1.5 text-sm"
        />
        <select
          data-testid="cms-photos-category"
          value={category} onChange={(e) => setCategory(e.target.value)}
          className="rounded-full border border-black/15 bg-white px-3 py-1.5 text-sm"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {tab === "local" && (
          <>
            <label
              data-testid="cms-photos-upload"
              className={
                "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase cursor-pointer " +
                (uploading
                  ? "bg-black/10 text-[#8E8B7F]"
                  : "bg-[color:var(--np-brand,#B85F2A)] text-white hover:opacity-90")
              }
            >
              <UploadSimple size={13} weight="bold" />
              {uploading ? "Uploading…" : "Upload"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
              />
            </label>
            <label
              data-testid="cms-photos-camera"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase cursor-pointer hover:bg-black/5"
            >
              <Camera size={13} weight="bold" />
              Camera
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
              />
            </label>
          </>
        )}
      </div>

      {tab === "local" ? (
        filteredLocal.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/15 bg-white/60 p-10 text-center text-[#3A3830]">
            No photos yet. Pick a category and upload from your computer or phone camera.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="cms-photos-grid-local">
            {filteredLocal.map((p) => (
              <div key={p.id} data-testid={`cms-photo-${p.id}`}
                   className="rounded-xl border border-black/10 bg-white overflow-hidden">
                <div className="aspect-[4/3] bg-black/5">
                  <img src={p.url} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {p.category}{p.source === "photopulse.pro" ? " · PP" : ""}
                  </div>
                  <div className="text-xs text-[#241209] line-clamp-2">{p.caption || p.credit || "—"}</div>
                  <button
                    data-testid={`cms-photo-remove-${p.id}`}
                    onClick={() => removeLocal(p.id)}
                    className="mt-1 text-[9px] font-mono-tech tracking-[0.22em] uppercase text-red-700 hover:underline"
                  >Remove</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        filteredRemote.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/15 bg-white/60 p-10 text-center text-[#3A3830]">
            No PhotoPulse photos available.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="cms-photos-grid-remote">
            {filteredRemote.map((p, i) => {
              const url = p.image_url || p.thumb_url;
              const id = p.id || i;
              return (
                <div key={id + "-" + i} className="rounded-xl border border-black/10 bg-white overflow-hidden"
                     data-testid={`cms-photo-remote-${id}`}>
                  <div className="aspect-[4/3] bg-black/5">
                    <img src={url} alt={p.title || ""} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">PhotoPulse</div>
                    <div className="text-xs text-[#241209] line-clamp-2">{p.title || p.caption || "—"}</div>
                    <button
                      data-testid={`cms-photo-import-${id}`}
                      onClick={() => importFromPP(p)}
                      disabled={importingId === (p.id || url) || !category}
                      title={!category ? "Pick a category first" : "Import to local library"}
                      className="mt-1 inline-flex items-center gap-1 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-2 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase disabled:opacity-50"
                    >
                      <DownloadSimple size={10} weight="bold" />
                      {importingId === (p.id || url) ? "…" : "Import"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
function ObservationsPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");

  const load = async () => {
    try {
      const { data } = await http.get(`/cms/pages/observations?status=${status}&limit=100`);
      setItems(data.observations || []);
    } catch (e) {
      toast.error("Failed to load observations");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const moderate = async (obs_id, next) => {
    try {
      await http.post(`/cms/pages/observations/${obs_id}/moderate`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Moderation failed");
    }
  };

  return (
    <div data-testid="cms-panel-observations">
      <div className="flex flex-wrap gap-2 mb-4">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  data-testid={`cms-obs-filter-${s}`}
                  className={
                    "rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase border " +
                    (status === s
                      ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent"
                      : "bg-white text-[#151312] border-black/15")
                  }>{s}</button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-black/15 bg-white/60 p-10 text-center text-[#3A3830]">
          No {status} observations.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((o) => (
            <li key={o.obs_id}
                data-testid={`cms-obs-row-${o.obs_id}`}
                className="rounded-2xl border border-black/10 bg-white p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[240px]">
                <div className="font-serif-display text-lg">{o.title || o.category}</div>
                <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">
                  {o.user_name || "Anonymous"} · {o.location || "—"} · {o.created_at?.slice(0, 10)}
                </div>
                {o.description && <div className="mt-1 text-sm text-[#3A3830] line-clamp-2">{o.description}</div>}
              </div>
              <div className="flex items-center gap-2">
                {status !== "approved" && (
                  <button data-testid={`cms-obs-approve-${o.obs_id}`}
                          onClick={() => moderate(o.obs_id, "approved")}
                          className="rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase hover:bg-emerald-100">
                    Approve
                  </button>
                )}
                {status !== "rejected" && (
                  <button data-testid={`cms-obs-reject-${o.obs_id}`}
                          onClick={() => moderate(o.obs_id, "rejected")}
                          className="rounded-full border border-red-200 bg-red-50 text-red-700 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase hover:bg-red-100">
                    Reject
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
